/* eslint-disable max-lines */
import { getOptimizedImagePath as imagePath } from '@abillionveg/abillionutilities'
import constants from '../constants/constants'

// capitalize all letters/the first letter of a string
export function capitalizeString(string, all = true) {
  if (!string) return ''
  const trimmed = string.trim()

  function sentenceCase(word) {
    return word.charAt(0).toUpperCase().concat(word.slice(1).toLowerCase())
  }

  return all
    ? trimmed.replace(/\w\S*/g, (m) => sentenceCase(m))
    : sentenceCase(trimmed)
}

export function handleFriendlinessScore(score) {
  switch (true) {
    case score >= 0 && score < 33:
      return constants.lists.vegFriendliness[0]
    case score >= 33 && score < 67:
      return constants.lists.vegFriendliness[1]
    case score >= 67:
      return constants.lists.vegFriendliness[2]
    default:
      return 'No veg*n rating'
  }
}

export const getResolutionFromImageSize = (size) => {
  const sizeMap = {
    xs: 120,
    sm: 240,
    md: 480,
    lg: 720,
    xl: 1080,
    xxl: 1920
  }

  const defaultSize = 720

  return sizeMap[size] ?? defaultSize
}

export function optimizedPath(path, size) {
  if (!path) {
    return ''
  }

  if (!size) {
    return path
  }

  return imagePath(path, getResolutionFromImageSize(size))
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export const getImageSrcSet = (path) => {
  const cloudinary = 'https://res.cloudinary.com/abillionveg/image/upload/'
  const cloudflare = 'https://imagedelivery.net/hhZzKJCQsEDsVEGQ2sIMyg/'

  if (!path) {
    return ''
  }

  const isCloudinary = path.includes(cloudinary)
  const isCloudflare = path.includes(cloudflare)

  if (isCloudflare) {
    return ['120', '240', '480', '720', '1080'].reduce((result, width) => {
      const newPath = path.replace('/public', `/w=${width}`)
      if (width !== '1080') {
        return `${result + newPath} ${width}w, `
      }
      return `${result + newPath} ${width}w`
    }, '')
  }

  if (isCloudinary) {
    const identifier = path.split(cloudinary)[1]
    return ['120', '240', '480', '720', '1080'].reduce((result, width) => {
      const newPath = `${cloudinary}f_webp,q_auto,w_${width}/${identifier}`
      if (width !== '1080') {
        return `${result + newPath} ${width}w, `
      }
      return `${result + newPath} ${width}w`
    }, '')
  }

  return path
}

export const optimizedVideoPath = (path) => {
  if (!path) {
    return ''
  }
  const cloudinary = 'https://res.cloudinary.com/abillionveg/video/upload/'

  if (!path.includes(cloudinary)) {
    return path
  }

  const identifier = path.split(cloudinary)[1]
  return `${cloudinary}q_auto,f_auto/${identifier}`
}

// from cloudflare it will return an array of variants
export const getCloudflareImagePath = (variants) => {
  if (variants.length > 1) {
    return variants.find((imgPath) => imgPath.includes('public'))
  }
  return variants[0]
}

// return user friendly review date
// TODO fix
// eslint-disable-next-line complexity,sonarjs/cognitive-complexity
export function formatDate(date) {
  const now = Date.now()
  const posted = Date.parse(date)
  const difference = (now - posted) / 1000 / 60 // number of minutes since review
  switch (true) {
    case Math.floor(difference) < 60:
      return `${Math.max(0, Math.floor(difference))} ${
        Math.floor(difference) === 1 ? 'MINUTE' : 'MINUTES'
      } AGO`
    case Math.floor(difference / 60) < 24:
      return `${Math.floor(difference / 60)} ${
        Math.floor(difference / 60) === 1 ? 'HOUR' : 'HOURS'
      } AGO`
    case Math.floor(difference / 60 / 24) < 7:
      return `${Math.floor(difference / 60 / 24)} ${
        Math.floor(difference / 60 / 24) === 1 ? 'DAY' : 'DAYS'
      } AGO`
    case Math.floor(difference / 60 / 24 / 7) <= 4:
      return `${Math.floor(difference / 60 / 24 / 7)} ${
        Math.floor(difference / 60 / 24 / 7) === 1 ? 'WEEK' : 'WEEKS'
      } AGO`
    case Math.floor(difference / 60 / 24 / 30) < 12:
      return `${Math.max(1, Math.floor(difference / 60 / 24 / 30))} ${
        Math.max(1, Math.floor(difference / 60 / 24 / 30)) === 1
          ? 'MONTH'
          : 'MONTHS'
      } AGO`
    case Math.floor(difference / 60 / 24 / 365) >= 1:
      return `${Math.max(1, Math.floor(difference / 60 / 24 / 365))} ${
        Math.max(1, Math.floor(difference / 60 / 24 / 365)) === 1
          ? 'YEAR'
          : 'YEARS'
      } AGO`
    default:
      return ''
  }
}

export function overallRating(review, type = 'dish', brandCategory) {
  if (type === 'dish') {
    return (
      constants.reviews.dish
        .map((category) => [review[category]])
        .reduce((a, b) => parseInt(a, 10) + parseInt(b, 10)) /
      constants.reviews.dish.length
    ).toFixed(1)
  }
  return (
    constants.reviews[brandCategory]
      .map((factor) => [review.rating[factor]])
      .reduce((a, b) => parseInt(a, 10) + parseInt(b, 10)) /
    constants.reviews[brandCategory].length
  )
}

// copy a string
export function copy(string = '') {
  if (typeof string !== 'string') {
    return false
  }
  const textField = document.createElement('textarea')
  textField.value = string
  document.body.appendChild(textField)
  textField.select()
  document.execCommand('copy')
  textField.remove()
  return true
}

export function isEmailValid(email) {
  return (
    !!email &&
    !!email.trim() &&
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
      email.trim()
    )
  )
}

export function displayDate(date) {
  if (!date) return ''
  return new Date(date).toUTCString().slice(4, 16)
}

export function calcAmbassadorEarnings(earnings, pending, paid) {
  return earnings - pending - paid
}

// TODO fix
// eslint-disable-next-line complexity
export function getRestaurantAddress(restaurant) {
  const address = (
    restaurant.neighborhood &&
    restaurant.neighborhood !== restaurant.sublocality_level_1
      ? `${restaurant.neighborhood}, `
      : ''
  )
    .concat(
      restaurant.sublocality_level_1 &&
        restaurant.sublocality_level_1 !== restaurant.locality
        ? `${restaurant.sublocality_level_1}, `
        : ''
    )
    .concat(
      restaurant.locality && restaurant.locality !== restaurant.country
        ? `${restaurant.locality}, `
        : ''
    )
    .concat(restaurant.country)

  return address
}

export function getReviewSourceType(type) {
  const source = type === 'dish' ? 'restaurant' : 'brand'
  return source
}

// TODO fix
// eslint-disable-next-line complexity,sonarjs/cognitive-complexity
export function transformRestaurantFromDB(restaurant) {
  if (!restaurant) return restaurant

  const transformedRestaurant = {
    businessId: restaurant.business,
    categories: restaurant.categories,
    contact: {
      email: restaurant.email,
      facebook: restaurant.facebook,
      instagram: restaurant.instagram,
      phone: restaurant.phone,
      twitter: restaurant.twitter,
      website: restaurant.website
    },
    createdAt: restaurant.createdAt,
    description: restaurant.description,
    dishesCount: restaurant.dishesCount,
    dishReviewsCount: restaurant.dishReviews,
    friendliness: restaurant.friendliness && restaurant.friendliness.average,
    geo: {
      country: {
        name: restaurant.country
      },
      displayAddress: restaurant.formatted_address,
      distance: restaurant.distance,
      locality: restaurant.locality || restaurant.city,
      location: restaurant.latitude &&
        restaurant.longitude && [restaurant.latitude, restaurant.longitude],
      neighborhood: restaurant.neighborhood,
      postal: restaurant.postal_code,
      region: restaurant.administrative_area_level_1,
      street:
        restaurant.street_number &&
        restaurant.route &&
        `${restaurant.street_number} ${restaurant.route}`
    },
    googlePlaceId: restaurant.google_place_id,
    hasDishes: restaurant.validDishIds && restaurant.validDishIds.length > 0,
    hours: restaurant.hours,
    id: restaurant._id,
    _id: restaurant._id,
    image: restaurant.image && restaurant.image.path,
    isActive: !restaurant.isClosed ? !restaurant.isClosed : undefined,
    name: restaurant.name,
    pokesCount: restaurant.votesCount,
    price: restaurant.price && restaurant.price.average,
    rating: restaurant.rating,
    recommendedScore: restaurant.recommendedScore,
    normalizedName: restaurant.normalizedName,
    slugId: restaurant.slugId,
    hexLocation: restaurant.hexLocation
  }

  return transformedRestaurant
}

export function transformDishFromDB(dish) {
  if (!dish) return dish

  const transformedDish = {
    allergens: dish.allergens,
    businessId: dish.business,
    createdAt: dish.createdAt,
    description: dish.description,
    id: dish._id,
    _id: dish._id,
    image: dish.image && dish.image.path,
    ingredients: dish.ingredients,
    isActive: dish.isActive,
    isVerified: dish.isVerified,
    name: dish.name,
    price: dish.price,
    rating: dish.ratings && dish.ratings.average,
    recommendedScore: dish.recommendedScore,
    restaurantId:
      dish.restaurants && dish.restaurants[0] && dish.restaurants[0]._id,
    reviewsCount: dish.reviews,
    vegType: dish.vegType,
    normalizedName: dish.normalizedName,
    restaurantNames: dish.restaurantNames,
    slugId: dish.slugId
  }

  return transformedDish
}

export function imageAlt(review) {
  if (review.type === 'general') {
    return `photo shared by @${
      review.user && review.user.username
    } on ${displayDate(review.createdAt)} - post`
  }

  return `photo of ${
    review[getReviewSourceType(review.type)] &&
    review[getReviewSourceType(review.type)].name
  } ${review[review.type] && review[review.type].name} shared by @${
    review.user && review.user.username
  } on ${displayDate(review.createdAt)} - review`
}
